import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { useTranslation } from 'react-i18next';

import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { Survey } from '../../../../models/Healthgrades';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import { mobileOnly } from '../../../ConstantsStyles';
import { Rating } from '../../../SnackCard/Rating';
import { dateTimeFormatToDate } from '../../../Utils/datesUtils/formatDate';
import {
  DateTextStyles,
  verbatimCard,
  verbatimTextStyles,
} from './PatientReviews.styled';

type Props = {
  survey: Survey | null;
  setReviewModal: (a: Survey) => void;
  setIsModalOpen?: (a: boolean) => void;
};

export const PatientVerbatimCard = ({
  survey,
  setReviewModal,
  setIsModalOpen,
}: Props) => {
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const mobileScreen = useMediaQuery(mobileOnly) || isWidget;
  const { t } = useTranslation();

  const submittedDate = () => {
    if (survey && survey.submittedDate)
      return dateTimeFormatToDate(survey.submittedDate);
    return '';
  };

  if (!survey) return null;

  const clampText = (text, max) => {
    if (text?.length >= max) {
      return '"' + text.slice(0, max).trim() + '...';
    }
    return `"${text}"`;
  };

  const isGEHAV2 = shouldUseGehaV2Styling();
  return (
    <Card
      css={verbatimCard(mobileScreen)}
      data-auto-testid={`review-card-${survey.id}`}
      data-testid={`review-card-${survey.id}`}
      key={survey.id}
      onClick={() => {
        setReviewModal(survey);
        if (setIsModalOpen) {
          setIsModalOpen(true);
        }
      }}
    >
      <Flex direction="column">
        <Flex alignItems="end" direction="row" justify="space-between">
          <Rating
            hideNumber
            outOf={5}
            rating={survey.averageRating}
            size={mobileScreen ? 16.67 : 20}
          />
          <Text css={DateTextStyles}>{submittedDate()}</Text>
        </Flex>

        <Flex
          alignContent="stretch"
          css={{
            'abyss-flex-root': {
              overflow: 'hidden',
              marginTop: '12px',
              lineHeight: '16px',
            },
          }}
          direction="column"
        >
          <Text css={verbatimTextStyles(mobileScreen, isGEHAV2)} size="$md">
            {clampText(survey.comment?.commentText, mobileScreen ? 80 : 106)}
            <Link
              aria-label={`${t('Full Review')} ${submittedDate().toString()}`}
              css={{
                marginLeft: '4px',
              }}
              data-auto-testid={`review-card-${survey.id}-link`}
            >
              {t('Full Review')}
            </Link>
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
};
